// Functions to track custom events for GA4

'use strict';

(function (window) {
  // Variables for embed YouTube video loading and tracking
  let youTubeIframeAPIPromise;
  const youTubeScriptURL = 'https://www.youtube.com/iframe_api';
  const youTubeScriptId = 'youtube-iframe-api';
  const youTubePlayers = new Set();
  const youTubePlayerProgressState = new WeakMap();

  // Initalize GA4 tracking for CTA custom clicks
  function initAnalyticsTracking() {
    const customUrlTracking = window.APU.customUrlTracking;
    if (customUrlTracking) {
      customUrlTracking.forEach((item) => {
        const $anchors = document.querySelectorAll(
          `a[href="${CSS.escape(item.landingPageUrl)}"]`,
        );
        $anchors.forEach((anchor) => {
          if (anchor.dataset.hasGaUrlTracker == undefined) {
            anchor.dataset.hasGaUrlTracker = true;
            anchor.addEventListener('click', function () {
              recordClickAnalytics(this, item.customTrackingLabel);
            });
          }
        });
      });
    }
  }

  // Record GA4 data for CTA custom clicks
  function recordClickAnalytics(link, customTrackingLabel) {
    if (window.gtag) {
      gtag('event', 'click_cta', {
        custom_tracking_label: customTrackingLabel,
        link_label: link.text,
        link_href: link.href,
      });
    }
  }

  // Load YouTube Iframe API as a Promise
  function loadYouTubeAPI() {
    if (youTubeIframeAPIPromise) return youTubeIframeAPIPromise;

    youTubeIframeAPIPromise = new Promise((resolve) => {
      // If YT already exists, resolve immediately
      if (window.YT && window.YT.Player) {
        resolve(window.YT);
        return;
      }

      // Otherwise, load the script
      const tag = document.createElement('script');
      tag.id = youTubeScriptId;
      tag.src = youTubeScriptURL;
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

      // Resolve once API is ready
      window.onYouTubeIframeAPIReady = () => resolve(window.YT);

      // Failure — script load error
      tag.onerror = () =>
        reject(new Error('YouTube Iframe API failed to load'));
    });

    return youTubeIframeAPIPromise;
  }

  // Record GA4 data for embed video events
  function sendGA4Event(eventName, player, extra = {}) {
    if (!window.gtag || !player) return;

    const data = player.getVideoData();
    const videoId = data?.video_id || '';
    const title = data?.title || '';
    const duration = Math.round(player.getDuration() || 0);
    const currentTime = Math.round(player.getCurrentTime() || 0);
    const percent =
      duration > 0 ? Math.round((currentTime / duration) * 100) : 0;

    gtag('event', eventName, {
      video_title: title,
      video_url: `https://www.youtube.com/watch?v=${videoId}`,
      video_provider: 'youtube',
      video_duration: duration,
      video_current_time: currentTime,
      video_percent: percent,
      ...extra,
    });
  }

  // Initialize youtube players with data
  function onYTPlayerReady(event) {
    const player = event.target;
    youTubePlayers.add(player);

    youTubePlayerProgressState.set(player, {
      started: false,
      completed: false,
      milestones: { 10: false, 25: false, 50: false, 75: false, 90: false },
      interval: null,
    });
  }

  // Send GA4 tracking events on youtube player state change
  function onYTPlayerStateChange(event) {
    const player = event.target;
    const state = event.data;
    const track = youTubePlayerProgressState.get(player);
    if (!track) return;

    if (state === YT.PlayerState.PLAYING) {
      if (!track.started) {
        track.started = true;
        sendGA4Event('video_start', player);
      }

      if (!track.interval) {
        track.interval = setInterval(() => {
          const duration = player.getDuration();
          const current = player.getCurrentTime();
          if (!duration) return;

          const percent = Math.floor((current / duration) * 100);
          [10, 25, 50, 75, 90].forEach((mark) => {
            if (percent >= mark && !track.milestones[mark]) {
              track.milestones[mark] = true;
              sendGA4Event('video_progress', player, { video_percent: mark });
            }
          });
        }, 1000);
      }
    }

    if (state === YT.PlayerState.PAUSED || state === YT.PlayerState.BUFFERING) {
      clearInterval(track.interval);
      track.interval = null;
    }

    if (state === YT.PlayerState.ENDED) {
      clearInterval(track.interval);
      track.interval = null;

      if (!track.completed) {
        track.completed = true;
        sendGA4Event('video_complete', player, { video_percent: 100 });
      }
    }
  }

  // Initalize video tracking events for YouTube embeds
  async function initVideoTracking(event) {
    const liteEl = event.target;
    const shadow = liteEl.shadowRoot;
    const iframe = shadow.querySelector('iframe');
    if (!iframe) return;

    // Wait for the YouTube API to be ready
    const YT = await loadYouTubeAPI();

    // create player
    new YT.Player(iframe, {
      events: {
        onReady: onYTPlayerReady,
        onStateChange: onYTPlayerStateChange,
      },
    });
  }

  // Event listeners for dynamically loaded youtube embeds
  document.addEventListener('liteYoutubeIframeLoaded', initVideoTracking);

  initAnalyticsTracking();
})(this);

// Record GA4 data
// https://developers.google.com/tag-platform/gtagjs/reference
function analyticsEvent(eventName, eventParams = {}) {
  if (window.gtag) {
    gtag('event', eventName, eventParams);
  } else {
    console.debug('[no gtag debug] event', eventName, eventParams);
  }
}
